/**
 * Sewing Factory Board — row helpers and density. Status colours/labels come from
 * `../sewing-line-status.ts`, shared with the floor board.
 */

export {
  LINE_STATUS_LABEL as STATUS_LABEL,
  lineStatusLabel,
  type StatusTone as Tone,
} from '../sewing-line-status';
export {
  efficiencyStatusTone,
  lineStatusTone,
  wftStatusTone,
  wipStatusTone,
} from '../sewing-line-status';

import {
  efficiencyStatusTone,
  lineStatusTone,
  wftStatusTone,
  wipStatusTone,
  type StatusTone,
} from '../sewing-line-status';

/** A row of any of the three views. Values arrive already in their declared type (§5.7). */
export type FactoryRow = Record<string, unknown>;

export function num(source: FactoryRow | undefined, field: string): number | null {
  if (source === undefined) return null;
  const value = source[field];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function str(source: FactoryRow | undefined, field: string): string {
  if (source === undefined) return '';
  const value = source[field];
  return typeof value === 'string' ? value : '';
}

export function pct(value: number | null, digits = 1): string {
  return value === null ? '—' : `${value.toFixed(digits)}%`;
}

/** Whole pieces / line counts — target so far can be fractional in SQL; display never is. */
export function count(value: number | null): string {
  return value === null ? '—' : Math.round(value).toLocaleString();
}

/** Bar geometry, clamped to the track. A floor over 100% fills it rather than overflowing. */
export function widthPct(value: number | null): string {
  return `${String(Math.max(0, Math.min(100, value ?? 0)))}%`;
}

/**
 * The board's densities. `useFittedPanels` measures a rendered panel and picks one; the column
 * count is part of the tier, because nine floors need three columns and two need two.
 * Discrete prebuilt class sets, never a computed font size — §4.8 allows runtime `style` for
 * geometry only.
 */
export type Tier = 'desk' | 'sm' | 'md' | 'lg' | 'xl';

/** Panel columns per tier. `desk` is the report-at-a-desk density, not a wall board. */
export const TIER_COLUMNS: Record<Tier, number> = {
  xl: 2,
  lg: 2,
  md: 2,
  sm: 3,
  desk: 2,
};

/** Row helpers — same maps as the floor board via `sewing-line-status`. */
export function effTone(row: FactoryRow | undefined): StatusTone {
  return efficiencyStatusTone(str(row, 'efficiency_status'));
}

export function lineTone(row: FactoryRow | undefined): StatusTone {
  return lineStatusTone(str(row, 'line_status'));
}

export function wftTone(row: FactoryRow | undefined): StatusTone {
  return wftStatusTone(str(row, 'wft_status'));
}

export function wipTone(row: FactoryRow | undefined): StatusTone {
  return wipStatusTone(str(row, 'wip_status'));
}

/** Gold, silver, bronze — first, second, third. Keyed by the rank the SERVER assigned. */
export const PODIUM: Record<number, string> = {
  1: 'text-medal-gold',
  2: 'text-medal-silver',
  3: 'text-medal-bronze',
};

/** Podium as a chip border — used on line chips so trophies never overlap kiosk layout. */
export const PODIUM_BORDER: Record<number, string> = {
  1: 'border-2 border-medal-gold',
  2: 'border-2 border-medal-silver',
  3: 'border-2 border-medal-bronze',
};
